// Speedtest-side resolver for glass-ui's `--motion-stagger-*` cascade tokens.
//
// Glass-ui publishes the staged-cascade canon at `styles/tokens.css`:
//
//   --motion-stagger-tight:   40ms   // dense row cascades
//   --motion-stagger-default: 80ms   // default per-bar / per-row beat
//   --motion-stagger-relaxed: 120ms  // per-series / cross-card cadence
//
// Several speedtest sites need the value as a NUMBER (echarts `animationDelay`
// expects ms, the `useStagger` composable expects `delayMs`, inline-style
// `calc()` paths multiply by index in JS). Cascading the token via
// `var(--motion-stagger-*)` works for pure-CSS consumers, but JS sites have
// to resolve at runtime — historically each consumer hand-rolled the same
// `STATS_CARD_STAGGER_MS / BAR_STAGGER_MS / SERIES_STAGGER_MS` constant +
// drift-risked it against the publisher canon (AK-A5 §6.7 → AL-A5 §5.1.a).
//
// This barrel resolves each token once at module load via the documentElement
// cascade and exports the parsed number. Fallback values match the publisher
// defaults so SSR / vitest jsdom (where the stylesheet may not be applied)
// still produce a sensible cadence — the moment glass-ui re-tunes the canon,
// every JS-side consumer picks up the new value via a single reload.

import type { TimingFunction } from "@mkbabb/keyframes.js";

const FALLBACK_MS: Readonly<Record<StaggerTier, number>> = {
    tight: 40,
    default: 80,
    relaxed: 120,
};

/**
 * The canonical timing curves the speedtest motion composables clock off,
 * as callable `TimingFunction`s — the value.js-free shape keyframes.js 4.x
 * accepts directly at `ManualTimeline({ easing })` / `NumericAnimation({
 * timingFunction })`.
 *
 * ── DDR — AV-W10 (2026-06-11): callable, not `resolveEasing(name)` ─────
 * keyframes.js 4.x made string easing NAMES a fail-explicit throw — a
 * `timingFunction: "easeOutCubic"` (and even `"linear"`) now raises
 * `AnimationOptionError`. The library offers two replacements: resolve the
 * name once via the async `resolveEasing(name)` (which lazy-loads the
 * value.js easing registry — the DYNAMIC boundary the light composables
 * otherwise never touch), or pass a callable / typed `Easing`.
 *
 * We pass callables. WHY: (1) the five call sites construct their clock
 * SYNCHRONOUSLY (`createPhaseTransition` builds its `ManualTimeline` inline;
 * `createMeterMilestone` is a sync factory) — `resolveEasing` is a `Promise`,
 * so routing through it would force an async ripple or a top-level `await`
 * that turns these hot, synchronously-imported composables into deferred
 * modules. (2) The coordination ledger pins speedtest to the LIGHT keyframes
 * exports only; `resolveEasing` would drag the value.js registry chunk into
 * the `Timeline` path the d.ts explicitly documents as "value.js-free".
 * (3) These three are the canonical Penner curves — universal, unambiguous
 * definitions value.js itself implements; defined ONCE here they are the
 * single source every consumer shares.
 *
 * IDENTITY PROOF (the curve is byte-identical — an API-shape repair, NOT a
 * semantics edit): sampled `resolveEasing("<name>").fn` from the installed
 * 4.1.0 against each callable below at 101 points t∈[0,1] — max abs diff
 * `1.110e-16` (IEEE-754 machine epsilon) for both cubics, `0` for linear.
 *
 * RE-LITIGATE-IF: a site needs a `css` string companion (a CSS-driven
 * cross-fade keyed off the same curve) — then a typed `Easing` (`{ fn, css }`)
 * via `resolveEasing`/`toEasing` earns its async cost; until then the bare
 * callable is the minimal, value.js-free handle.
 */

/** `t => t` — the identity curve (the un-eased ramp). */
export const linear: TimingFunction = (t) => t;

/** Penner ease-out cubic: fast attack, soft decay. */
export const easeOutCubic: TimingFunction = (t) => 1 - Math.pow(1 - t, 3);

/** Penner ease-in-out cubic: symmetric S — mirrored attack and decay. */
export const easeInOutCubic: TimingFunction = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export type StaggerTier = "tight" | "default" | "relaxed";

function readTokenMs(name: string, fallback: number): number {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return fallback;
    }
    const raw = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
    if (!raw) return fallback;
    // `--motion-stagger-*` tokens carry an explicit `ms` unit. Strip it and
    // parseFloat so the consumer pattern (`idx * MOTION_STAGGER_MS.tight`)
    // composes with raw numeric arithmetic.
    const num = parseFloat(raw);
    return Number.isFinite(num) ? num : fallback;
}

/**
 * Resolved `--motion-stagger-*` cadence in ms. Module-load resolution: the
 * map is a frozen snapshot of the cascade at first import, so consumers can
 * destructure or index without paying a `getComputedStyle` cost per use.
 *
 * Re-tunes flow through publisher canon at glass-ui's `tokens.css` — a
 * hot-reload reseats the module and the new cadence propagates to every
 * consumer reachable from this barrel.
 */
export const MOTION_STAGGER_MS: Readonly<Record<StaggerTier, number>> =
    Object.freeze({
        tight: readTokenMs("--motion-stagger-tight", FALLBACK_MS.tight),
        default: readTokenMs("--motion-stagger-default", FALLBACK_MS.default),
        relaxed: readTokenMs("--motion-stagger-relaxed", FALLBACK_MS.relaxed),
    });

/**
 * Convenience accessor for inline-style call sites that prefer a function
 * call over an indexed read.
 */
export function staggerMs(tier: StaggerTier): number {
    return MOTION_STAGGER_MS[tier];
}

/**
 * Parse a CSS time token (`"450ms"`, `"0.45s"`) into milliseconds. Returns
 * `null` for any unparseable input so the caller can fall through to its
 * own fallback (the design constant a token resolves to). Negative values
 * survive the regex but are returned as-is — CSS allows them, and the
 * decision to clamp belongs to the consumer, not the parser.
 *
 * ── DDR — AO-T2 (2026-05-28) ─────────────────────────────────────────
 * Hoisted from `usePhaseTransition.ts` (lines 63-71) and
 * `useCompletionChoreography.ts` (lines 133-139) where it lived as a
 * private duplicate. The two copies were semantic-identical (an extra
 * empty-string guard in `usePhaseTransition` was redundant — the regex
 * already returns `null` on `""`); the audit (A3 §245, §320) called it
 * "private-copied verbatim" and flagged the drift risk: a unit-handling
 * tweak (`vmin`, `calc(...)`, etc.) would have to be patched in two
 * places. `src/styles/motion.ts` already owns CSS-time-token resolution
 * (`MOTION_STAGGER_MS`, `readTokenMs`), so this barrel is the natural
 * home — one parser, one regex, one place to evolve.
 *
 * RE-LITIGATE-IF: a third consumer surfaces with a divergent need
 * (parametric unit-set, `calc()` support, vector returns). Confirm the
 * signature still matches before adding a consumer; consider
 * parameterising or splitting if a variant arises.
 */
export function parseCssTimeMs(raw: string): number | null {
    const match = /^(-?[\d.]+)\s*(ms|s)?$/.exec(raw.trim());
    if (!match) return null;
    const n = Number(match[1]);
    if (!Number.isFinite(n)) return null;
    return match[2] === "s" ? n * 1000 : n;
}
